import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-62d18508"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "cropper-wrapper" }
const _hoisted_2 = ["src"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "cropper-dark" }, null, -1))
const _hoisted_4 = ["src"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "crop-area-border" }, null, -1))
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "crop-area-target" }, null, -1))
const _hoisted_7 = { class: "cropper-controls" }

import BaseModal from '@components/modals/BaseModal';
import { computed, onMounted, ref, watch } from 'vue';
import { useWindowSizes } from '@/hooks/use-window-sizes';
import Button from '@ui-kit/controls/button/Button.vue';
import { useModalControls } from '@components/modals/hooks/use-modal-controls';

enum DIRECTION {
  horizontal,
  vertical,
}

interface Emits {
  (e: 'crop', blob: Blob): void,
}
interface Props {
  imageSrc: string,
  ratio?: [number, number],
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ModalImageCropper',
  props: {
    imageSrc: null,
    ratio: { default: () => [1, 1] }
  },
  emits: ["crop"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'crop', blob: Blob): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { imageSrc: string, ratio: [number, number] }

const H_SPACING = 16 * 6;
const V_SPACING = 16 * 12;





const imageWidth = ref<number>(1);
const imageHeight = ref<number>(1);
const imageRef = ref<HTMLImageElement>();
const offset = ref<number>(0);

const { clientWidth, clientHeight } = useWindowSizes();

const imageSizes = computed(() => {
  const cWidth = clientWidth.value - H_SPACING;
  const cHeight = clientHeight.value - V_SPACING;

  const imageRatio = imageWidth.value / imageHeight.value;
  if (imageWidth.value > cWidth || imageHeight.value > cHeight) {
    const windowRatio = cWidth / cHeight;

    return imageRatio > windowRatio ? [
        cWidth,
        cWidth / imageRatio,
    ] : [
        cHeight * imageRatio,
        cHeight,
    ];
  } else if (imageWidth.value < cWidth / 2) {
    return [
      cWidth / 2,
      (cWidth / 2) / imageRatio,
    ];
  }

  return [imageWidth.value, imageHeight.value];
});

const direction = computed<DIRECTION>(() => {
  const ratio = props.ratio[0] / props.ratio[1];
  const imageRatio = imageWidth.value / imageHeight.value;

  return imageRatio > ratio ? DIRECTION.horizontal : DIRECTION.vertical;
});

const cropOffsets = computed(() => {
  const ratio = props.ratio[0] / props.ratio[1];

  if (direction.value === DIRECTION.vertical) {
    return {
      left: 0,
      top: offset.value,
      width: imageSizes.value[0],
      height: (imageSizes.value[0] / ratio),
    };
  }
  return {
    top: 0,
    left: offset.value,
    width: (imageSizes.value[1] * ratio),
    height: imageSizes.value[1],
  };
});

const cropAreaStyles = computed(() => {
  return {
    cropper: {
      left: cropOffsets.value.left + 'px',
      top: cropOffsets.value.top + 'px',
      width: cropOffsets.value.width + 'px',
      height: cropOffsets.value.height + 'px',
    },
    image: {
      width: imageSizes.value[0] + 'px',
      height: imageSizes.value[1] + 'px',
      top: (-cropOffsets.value.top) + 'px',
      left: (-cropOffsets.value.left) + 'px',
    },
  };
});

function imageInit(iWidth: number, iHeight: number){
  imageWidth.value = iWidth;
  imageHeight.value = iHeight;
}

watch(imageSizes, () => {
  offset.value = 0;
});
onMounted(() => {
  const image = imageRef.value;
  if (image) {
    image.onload = () => imageInit(image.naturalWidth, image.naturalHeight);
  }
});

let startMouseCoord = 0;
let startOffsetValue = 0;
function onMouseDown(event: MouseEvent) {
  startOffsetValue = offset.value;
  if (direction.value === DIRECTION.vertical) {
    startMouseCoord = event.clientY;
  } else {
    startMouseCoord = event.clientX;
  }

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}
function onMouseMove(event: MouseEvent) {
  const newMc = direction.value === DIRECTION.vertical ? event.clientY : event.clientX;
  const limit = direction.value === DIRECTION.vertical
      ? imageSizes.value[1] - cropOffsets.value.height
      : imageSizes.value[0] - cropOffsets.value.width;
  offset.value = startOffsetValue + (newMc - startMouseCoord);
  if (offset.value < 0) {
    offset.value = 0;
  } else if (offset.value > limit) {
    offset.value = limit;
  }
}
function onMouseUp() {
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
}

const { close } = useModalControls();

function onCrop() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const image = document.createElement('img');

  const scale = imageWidth.value / imageSizes.value[0];
  const {left, top, height, width} = cropOffsets.value;

  canvas.width = width * scale;
  canvas.height = height * scale;

  if (ctx) {
    image.src = props.imageSrc;
    image.onload = () => {
      ctx.drawImage(image,
          left*scale, top*scale,
          width*scale, height*scale,
          0,0,
          canvas.width, canvas.height,
      );
      canvas.toBlob((blob) => {
        if (blob) {
          emit('crop', blob);
          close();
        }
      })
    }
  }
}
function onClose() {
  close();
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseModal), {
    "no-body-padding": "",
    "hide-header": "",
    "hide-close-button": ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "cropper",
          style: _normalizeStyle({width: `${_unref(imageSizes)[0]}px`, height: `${_unref(imageSizes)[1]}px`})
        }, [
          _createElementVNode("img", {
            ref_key: "imageRef",
            ref: imageRef,
            src: __props.imageSrc,
            class: "w-full h-full"
          }, null, 8, _hoisted_2),
          _hoisted_3,
          _createElementVNode("div", {
            class: "crop-area",
            onMousedown: onMouseDown,
            style: _normalizeStyle(_unref(cropAreaStyles).cropper)
          }, [
            _createElementVNode("img", {
              src: __props.imageSrc,
              class: "crop-area-image",
              style: _normalizeStyle(_unref(cropAreaStyles).image)
            }, null, 12, _hoisted_4),
            _hoisted_5,
            _hoisted_6
          ], 36)
        ], 4),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(Button, {
            onClick: onClose,
            label: _ctx.$t('common.actions.cancel'),
            "button-type": "outline",
            size: "lg"
          }, null, 8, ["label"]),
          _createVNode(Button, {
            onClick: onCrop,
            label: _ctx.$t('components.image-upload.crop'),
            size: "lg"
          }, null, 8, ["label"])
        ])
      ])
    ]),
    _: 1
  }))
}
}

})